import React, { useState } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';

export interface IBusinessUnitProps {
	backgroundImage?: IImage;
	heading?: string;
	/**
	 * @control textarea
	 */
	description?: string;
	ctaButton?: ILink;
}

export interface IBUComponentProps extends IBaseElementProps {
	mainHeading?: string;
	/**
	 * @control textarea
	 */
	mainDescription?: string;
	logo?: IImage;
	businessUnits?: IBusinessUnitProps[];
}

/**
 * Calian BU Component
 * @icon format-gallery
 * @block
 */
const BUComponent: React.FC<IBUComponentProps> = ({
	id,
	className,
	classNames = [],
	mainHeading,
	mainDescription,
	logo,
	businessUnits = [],
}) => {
	const [activeItem, setActiveItem] = useState(null);

	return (
		<div
			id={id}
			className={className || cn('calian-bu-component', ...classNames)}
		>
			<div className="calian-bu-component__container container-fluid mw-1260px">
				<div className="calian-bu-component__top row px-3 px-md-4 mb-6 mb-md-4">
					<div className="calian-bu-component__top--left col-12 col-md-7">
						{mainHeading && (
							<h2 className="calian-bu-component__main-heading mb-2 pb-1">
								{mainHeading}
							</h2>
						)}
						{mainDescription && (
							<p className="calian-bu-component__main-description fs-5 ls-1">
								{mainDescription}
							</p>
						)}
					</div>
					<div className="calian-bu-component__top--right col-12 col-md-5 d-flex flex-row justify-content-center justify-content-md-end">
						{logo && (
							<div className="calian-bu-component__logo mt-2">
								<Image className="img-fluid" {...logo} />
							</div>
						)}
					</div>
				</div>
				<div className="row calian-bu-component__bottom">
					{businessUnits && businessUnits.length > 0 && (
						<>
							<div className="calian-bu-component__business-unit-listing d-none d-lg-flex align-items-center">
								{businessUnits.map(
									(
										{
											backgroundImage,
											heading,
											description,
											ctaButton,
										},
										index,
									) => (
										<div
											key={index}
											className="calian-bu-component__business-unit-item"
											style={{
												backgroundImage: `url(${backgroundImage?.src})`,
											}}
											tabIndex={0}
										>
											<div className="calian-bu-component__business-unit-item--overlay h-100 d-flex flex-column justify-content-end ps-5">
												<div className="calian-bu-component__business-unit-item--content">
													{heading && (
														<h3 className="calian-bu-component__business-unit-item--heading text-white mb-2">
															{heading}
														</h3>
													)}
													{description && (
														<p className="calian-bu-component__business-unit-item--description text-white mt-3 mb-4">
															{description}
														</p>
													)}
													{ctaButton?.label && (
														<div className="calian-bu-component__business-unit-item--cta-button">
															<Button
																className="btn btn-sm btn-primary btn-primary-global"
																{...ctaButton}
															/>
														</div>
													)}
												</div>
											</div>
										</div>
									),
								)}
							</div>
							<div className="calian-bu-component__business-unit-listing mobile-listing d-flex d-lg-none flex-column align-items-center">
								{businessUnits.map(
									(
										{
											backgroundImage,
											heading,
											description,
											ctaButton,
										},
										index,
									) => (
										<button
											className="d-block w-100 border-0 p-0 text-decoration-none text-start"
											key={index}
											onClick={() =>
												setActiveItem(
													index === activeItem
														? null
														: index,
												)
											}
										>
											<div
												className={cn(
													'calian-bu-component__business-unit-item',
													{
														'active-item':
															index ===
															activeItem,
													},
												)}
												style={{
													backgroundImage: `url(${backgroundImage?.src})`,
												}}
												tabIndex={0}
											>
												<div className="calian-bu-component__business-unit-item--overlay h-100 d-flex flex-column justify-content-center align-items-start px-3">
													<div className="calian-bu-component__business-unit-item--content">
														{heading && (
															<h3 className="calian-bu-component__business-unit-item--heading text-white fs-5 fs-sm-3 mb-0">
																{heading}
															</h3>
														)}
														{description && (
															<p className="calian-bu-component__business-unit-item--description text-white fs-7 fs-sm-6 pe-14">
																{description}
															</p>
														)}
														{ctaButton?.label && (
															<div className="calian-bu-component__business-unit-item--cta-button">
																<Button
																	className="btn btn-sm btn-primary btn-primary-global fs-8 fs-sm-7 mt-2 px-3"
																	{...ctaButton}
																/>
															</div>
														)}
													</div>
												</div>
											</div>
										</button>
									),
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default BUComponent;
